import type { DimensionVariable } from '@/graphql/types/dimensions';
import { Table, TableBody, TableRow, Paper } from '@mui/material';
import {
  StyledHeaderCell,
  StyledTableCell,
  StyledTableContainer,
  StyledTableHead,
  StyledTableRow,
  VariableHeaderCell,
  VariableTableCell,
} from './styles';
import { useLocation } from 'react-router-dom';
import { useTranslation } from '@/hooks/useTranslation';

export interface Municipality {
  name_municipality: string;
  municipality_code: string;
}

interface DimensionVariableComparativeProps {
  dimensionVariablesComparative: {
    municipalities: Municipality[];
    opportunityVariables: DimensionVariable[];
    riskVariables: DimensionVariable[];
  };
  variant: 'opportunity' | 'risk';
}

export const DimensionVariableComparative = ({
  dimensionVariablesComparative,
  variant,
}: DimensionVariableComparativeProps) => {
  const { t } = useTranslation();

  const location = useLocation();
  const params = location.pathname;
  const municipalityCode = params.split('/').pop();

  const { municipalities, opportunityVariables, riskVariables } = dimensionVariablesComparative;

  // Seleciona as variáveis baseado no variant
  const variables = variant === 'opportunity' ? opportunityVariables : riskVariables;

  // Função para obter o valor de uma variável para um município específico
  const getVariableValueForMunicipality = (
    variable: DimensionVariable,
    municipalityCode: string
  ): string => {
    const value = variable.values?.find((v) => v.municipality_code === municipalityCode);
    return value?.formatted_value || '-';
  };

  if (!municipalities || municipalities.length === 0) {
    return null;
  }

  if (!variables || variables.length === 0) {
    return null;
  }

  const isCurrentMunicipality = (code: string) => code === municipalityCode;

  return (
    <StyledTableContainer component={Paper}>
      <Table>
        <StyledTableHead>
          <TableRow>
            <VariableHeaderCell>{t('table.table.dimension_variable.name')}</VariableHeaderCell>
            {municipalities.map((municipality) => (
              <StyledHeaderCell
                key={municipality.municipality_code}
                isHighlighted={isCurrentMunicipality(municipality.municipality_code)}
              >
                {municipality.name_municipality}
              </StyledHeaderCell>
            ))}
          </TableRow>
        </StyledTableHead>
        <TableBody>
          {variables.map((variable, index) => (
            <StyledTableRow key={`${variable.name}-${index}`}>
              <VariableTableCell>{variable.name}</VariableTableCell>
              {municipalities.map((municipality) => (
                <StyledTableCell
                  key={municipality.municipality_code}
                  isHighlighted={isCurrentMunicipality(municipality.municipality_code)}
                >
                  {getVariableValueForMunicipality(variable, municipality.municipality_code)}
                </StyledTableCell>
              ))}
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};
